// TODO: vitest component test (pages are covered by Playwright e2e)
import { useParcelExtractionEnabled } from '@/hooks/useParcelExtractionEnabled'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, FormActions, Input, Select, SearchableDropdown } from '@/components/ui'
import { FileUpload } from '@/components/ui/FileUpload'
import { useParcel, useCreateParcel, useUpdateParcel } from '@/hooks/useParcels'
import { useProperties } from '@/hooks/useProperties'
import { useCustomers } from '@/hooks/useCustomers'
import { usePropertyUnits } from '@/hooks/useUnits'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import { api } from '@/lib/api'
import type { Customer } from '@/types'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'

async function uploadParcelImage(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  return `/api/images/${presigned.key}`
}

const DEFAULT_CARRIERS = [
  'Thailand Post',
  'Kerry Express',
  'Flash Express',
  'J&T Express',
  'Shopee Express',
  'Lazada Express',
]

interface ParcelFormData {
  property_id: string
  customer_id: string
  unit_id: string
  recipient_name: string
  tracking_number: string
  carrier: string
  custom_carrier: string
  description: string
  received_at: string
}

export function ParcelFormPage() {
  const { t } = useTranslation('parcels')
  const { t: tc } = useTranslation('common')
  const parcelTextExtractionEnabled = useParcelExtractionEnabled()
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const parcelId = Number(id)
  const navigate = useNavigate()
  const translateError = useApiErrorTranslator('parcels')
  const addToast = useNotificationStore((s) => s.addToast)

  const { data: parcel, isLoading: isLoadingParcel } = useParcel(
    isEditing ? parcelId : 0
  )
  const createParcel = useCreateParcel()
  const updateParcel = useUpdateParcel()
  const { data: propertiesData } = useProperties(1)
  const { data: accountData } = useAccountSettings()
  const { timezone } = useLocalizationConfig()

  const [customerSearch, setCustomerSearch] = useState('')
  const [isOtherCarrier, setIsOtherCarrier] = useState(false)
  const [customerPage, setCustomerPage] = useState(1)
  const [accumulatedCustomers, setAccumulatedCustomers] = useState<Customer[]>([])

  // null means "not yet edited by user" — derive from server data
  const [localImageUrls, setImageUrls] = useState<string[] | null>(null)
  const imageUrls = localImageUrls ?? (isEditing && parcel ? (parcel.image_urls ?? []) : [])

  const carriers = accountData?.settings?.parcel_carriers?.length
    ? accountData.settings.parcel_carriers
    : DEFAULT_CARRIERS

  const carrierOptions = [
    { value: '', label: t('placeholders.carrier') },
    ...carriers.map((c) => ({ value: c, label: c })),
    { value: '__other__', label: t('carriers.other') },
  ]

  const properties = propertiesData?.data || []

  const propertyOptions = [
    { value: '', label: t('placeholders.select_property') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ParcelFormData>({
    defaultValues: {
      property_id: '',
      customer_id: '',
      unit_id: '',
      recipient_name: '',
      tracking_number: '',
      carrier: '',
      custom_carrier: '',
      description: '',
      received_at: localToday(timezone),
    },
  })

  const carrierValue = watch('carrier')
  const propertyIdValue = watch('property_id')
  const selectedPropertyId = propertyIdValue ? Number(propertyIdValue) : undefined

  // Cascade-filter customers by the chosen property so the operator sees
  // tenants who actually live there. Without this scope, fresh accounts with
  // wizard-created draft leases saw an empty dropdown (status='active' used
  // to be passed here too, which required signed_at IS NOT NULL).
  const { data: customersData, isLoading: isLoadingCustomers } = useCustomers(
    customerPage, undefined, customerSearch || undefined, selectedPropertyId,
  )

  const { data: unitsData } = usePropertyUnits(selectedPropertyId ?? 0)

  // Reset page and accumulated list when search query changes
  useEffect(() => {
    setCustomerPage(1)
    setAccumulatedCustomers([])
  }, [customerSearch])

  // Append new page results to accumulated list; replace on page 1
  useEffect(() => {
    if (!customersData?.data) return
    setAccumulatedCustomers((prev) => {
      if (customerPage === 1) return customersData.data
      const existingIds = new Set(prev.map((c) => c.id))
      const newItems = customersData.data.filter((c) => !existingIds.has(c.id))
      return [...prev, ...newItems]
    })
  }, [customersData, customerPage])

  const customerOptions = (() => {
    const opts = accumulatedCustomers.map((c) => ({ value: String(c.id), label: c.name }))
    if (isEditing && parcel?.customer && !accumulatedCustomers.find((c) => c.id === parcel.customer_id)) {
      opts.push({ value: String(parcel.customer_id), label: parcel.customer.name })
    }
    return opts
  })()

  const unitOptions = [
    { value: '', label: t('placeholders.select_unit') },
    ...(unitsData?.data ?? []).map((u) => ({
      value: String(u.id),
      label: u.number ?? u.name,
    })),
  ]

  // Reset customer and unit when property changes (only for new parcels)
  useEffect(() => {
    if (!isEditing && propertyIdValue) {
      setValue('customer_id', '')
      setValue('unit_id', '')
      setCustomerSearch('')
    }
  }, [propertyIdValue, isEditing, setValue])

  // Track "Other" carrier selection
  useEffect(() => {
    setIsOtherCarrier(carrierValue === '__other__')
  }, [carrierValue])

  // Populate form when editing
  useEffect(() => {
    if (isEditing && parcel) {
      setValue('property_id', String(parcel.property_id))
      setValue('customer_id', parcel.customer_id ? String(parcel.customer_id) : '')
      setValue('unit_id', parcel.unit_id ? String(parcel.unit_id) : '')
      setValue('recipient_name', parcel.recipient_name ?? '')
      setValue('tracking_number', parcel.tracking_number || '')
      setValue('description', parcel.description || '')
      setValue('received_at', parcel.received_at.split('T')[0])

      if (parcel.carrier) {
        const isKnown = carriers.includes(parcel.carrier)
        if (isKnown) {
          setValue('carrier', parcel.carrier)
        } else {
          setValue('carrier', '__other__')
          setValue('custom_carrier', parcel.carrier)
          setIsOtherCarrier(true)
        }
      }
    }
  }, [isEditing, parcel, carriers, setValue])

  const onSubmit = async (data: ParcelFormData) => {
    const resolvedCarrier =
      data.carrier === '__other__' ? data.custom_carrier : data.carrier

    try {
      if (isEditing) {
        await updateParcel.mutateAsync({
          id: parcelId,
          customer_id: data.customer_id ? Number(data.customer_id) : null,
          unit_id: data.unit_id ? Number(data.unit_id) : null,
          recipient_name: data.recipient_name || undefined,
          tracking_number: data.tracking_number || undefined,
          carrier: resolvedCarrier || undefined,
          description: data.description || undefined,
          image_urls: imageUrls,
        })
        navigate(`/parcels/${parcelId}`)
      } else {
        const result = await createParcel.mutateAsync({
          property_id: Number(data.property_id),
          customer_id: data.customer_id ? Number(data.customer_id) : null,
          unit_id: data.unit_id ? Number(data.unit_id) : null,
          recipient_name: data.recipient_name || undefined,
          tracking_number: data.tracking_number || undefined,
          carrier: resolvedCarrier || undefined,
          description: data.description || undefined,
          received_at: data.received_at,
          image_urls: imageUrls,
        })
        navigate(`/parcels/${result.id}`)
      }
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  if (isEditing && isLoadingParcel) {
    return (
        <div className="space-y-6">
          <Card>
            <div className="h-64 animate-pulse bg-base-200 rounded" />
          </Card>
        </div>
    )
  }

  return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('title'), href: '/parcels' },
            ...(isEditing && parcel
              ? [
                  {
                    label: parcel.tracking_number || `Parcel #${parcel.id}`,
                    href: `/parcels/${parcelId}`,
                  },
                ]
              : []),
            { label: isEditing ? t('edit_parcel') : t('new_parcel') },
          ]}
        />

        <PageHeader
          title={isEditing ? t('edit_parcel') : t('new_parcel')}
        />

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Select
                label={t('labels.property')}
                error={errors.property_id?.message}
                options={propertyOptions}
                disabled={isEditing}
                required
                {...register('property_id', {
                  required: tc('errors.required'),
                  validate: (v) => v !== '' || tc('errors.required'),
                })}
              />
              <Input
                label={t('labels.recipient_name')}
                placeholder={t('placeholders.recipient_name')}
                error={errors.recipient_name?.message}
                {...register('recipient_name')}
              />
              <SearchableDropdown
                name="customer_id"
                label={t('labels.customer')}
                error={errors.customer_id?.message}
                options={customerOptions}
                value={watch('customer_id')}
                onChange={(val) => setValue('customer_id', val, { shouldValidate: true })}
                onSearch={setCustomerSearch}
                isLoading={isLoadingCustomers}
                placeholder={t('placeholders.select_customer')}
                disabled={isEditing || !selectedPropertyId}
                hasMore={
                  !!customersData?.meta &&
                  customersData.meta.current_page < customersData.meta.total_pages
                }
                onLoadMore={() => setCustomerPage((p) => p + 1)}
              />
              <Select
                label={t('labels.unit')}
                error={errors.unit_id?.message}
                options={unitOptions}
                disabled={isEditing || !selectedPropertyId}
                {...register('unit_id')}
              />
              <Input
                label={t('labels.tracking_number')}
                placeholder={t('placeholders.tracking_number')}
                error={errors.tracking_number?.message}
                {...register('tracking_number')}
              />
              <div className="space-y-2">
                <Select
                  label={t('labels.carrier')}
                  error={errors.carrier?.message}
                  options={carrierOptions}
                  {...register('carrier')}
                />
                {isOtherCarrier && (
                  <Input
                    placeholder={t('placeholders.custom_carrier')}
                    error={errors.custom_carrier?.message}
                    required={isOtherCarrier}
                    {...register('custom_carrier', {
                      required: isOtherCarrier ? tc('errors.required') : false,
                    })}
                  />
                )}
              </div>
              <Input
                label={t('labels.description')}
                placeholder={t('placeholders.description')}
                error={errors.description?.message}
                {...register('description')}
              />
              <Input
                label={t('labels.received_at')}
                type="date"
                error={errors.received_at?.message}
                disabled={isEditing}
                required
                {...register('received_at', {
                  required: tc('errors.required'),
                })}
              />
            </div>

            {/* Parcel Photos */}
            <div className="space-y-1">
              <FileUpload
                label={t('labels.photos')}
                value={imageUrls}
                onChange={setImageUrls}
                maxFiles={5}
                accept="image/*"
                onUpload={uploadParcelImage}
              />
              {parcelTextExtractionEnabled && (
                <p
                  className="text-sm text-base-content/60"
                  data-testid="extraction-photo-hint"
                >
                  {t('extraction.photo_hint')}
                </p>
              )}
            </div>

            <FormActions>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(-1)}
              >
                {tc('actions.cancel')}
              </Button>
              <Button
                type="submit"
                loading={createParcel.isPending || updateParcel.isPending}
              >
                {isEditing ? tc('actions.save') : t('new_parcel')}
              </Button>
            </FormActions>
          </form>
        </Card>
      </div>
  )
}
